import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';
import '../../Marketplace/Marketplace.css';

const SEV_COLORS = {
  CRITICAL: { bg: '#fee2e2', text: '#991b1b' },
  HIGH:     { bg: '#ffedd5', text: '#9a3412' },
  MEDIUM:   { bg: '#fef3c7', text: '#92400e' },
  LOW:      { bg: '#e0e7ff', text: '#3730a3' },
};
const TICKET_STATUS_COLORS = {
  OPEN:       { bg: '#dbeafe', text: '#1e40af' },
  IN_PROGRESS:{ bg: '#fef3c7', text: '#92400e' },
  RESOLVED:   { bg: '#d1fae5', text: '#065f46' },
  VERIFIED:   { bg: '#d1fae5', text: '#065f46' },
};
const COVERAGE_COLORS = {
  AUTO:   { bg: '#d1fae5', text: '#065f46' },
  MANUAL: { bg: '#fef3c7', text: '#92400e' },
};
function Badge({ value, map }) {
  const c = (map[value]) || { bg: '#f1f5f9', text: '#475569' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: c.bg, color: c.text }}>
      {value || '—'}
    </span>
  );
}

export default function ThreatIntel() {
  const { t } = useTranslation();
  const COVERAGE_LABEL = { AUTO: t('ThreatIntel.coverageLabelAuto'), MANUAL: t('ThreatIntel.coverageLabelManual') };
  const [seeds, setSeeds]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState('');
  const [search, setSearch]     = useState('');
  const [sevFilter, setSevFilter] = useState('');
  const [selected, setSelected] = useState(null);

  // org-scoped lens
  const [responding, setResponding] = useState(false);
  const [respondResult, setRespondResult] = useState(null);
  const [tickets, setTickets]   = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const fetchSeeds = useCallback(() => {
    setLoading(true);
    axiosInstance.get('/iasm/threat-seeds')
      .then(res => setSeeds(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMsg(t('ThreatIntel.errorLoadCatalog')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { fetchSeeds(); }, [fetchSeeds]);

  const fetchTickets = useCallback((projectId) => {
    if (!projectId) return;
    axiosInstance.get(`/remediation/projects/${projectId}/tickets`)
      .then(res => setTickets(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const runRespond = async () => {
    setResponding(true); setMsg('');
    try {
      const res = await axiosInstance.post('/iasm/threat-seeds/respond');
      setRespondResult(res.data);
      fetchTickets(res.data?.remediationProjectId);
    } catch {
      setMsg(t('ThreatIntel.errorRespondFailed'));
    } finally {
      setResponding(false);
    }
  };

  const runScan = async () => {
    setScanning(true); setMsg('');
    try {
      const res = await axiosInstance.post('/iasm/threat-seeds/scan');
      setScanResult(res.data);
    } catch {
      setMsg(t('ThreatIntel.errorScanFailed'));
    } finally {
      setScanning(false);
    }
  };

  const filtered = seeds.filter(s => {
    const q = search.toLowerCase();
    const okq = !q || s.cveId?.toLowerCase().includes(q) || s.title?.toLowerCase().includes(q);
    const oks = !sevFilter || s.severity === sevFilter;
    return okq && oks;
  });

  const counts = seeds.reduce((a, s) => { a.total++; a[s.severity] = (a[s.severity] || 0) + 1; if (s.status === 'ACTIVE') a.active++; if (s.coverage === 'AUTO') a.auto++; return a; }, { total: 0, active: 0, auto: 0 });

  return (
    <div className="mp-wrap">
      <div className="mp-header">
        <div>
          <h2 className="mp-title"><i className="fas fa-radiation" /> {t('ThreatIntel.pageTitle')}</h2>
          <p className="mp-subtitle">
            {t('ThreatIntel.subtitle', { count: counts.total })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="mp-btn-secondary" onClick={runScan} disabled={scanning}>
            {scanning ? <><i className="fas fa-spinner fa-spin" /> {t('ThreatIntel.scanning')}</> : <><i className="fas fa-radar" /> {t('ThreatIntel.scanButton')}</>}
          </button>
          <button className="mp-btn-primary" onClick={runRespond} disabled={responding}>
            {responding ? <><i className="fas fa-spinner fa-spin" /> {t('ThreatIntel.responding')}</> : <><i className="fas fa-shield-virus" /> {t('ThreatIntel.respondButton')}</>}
          </button>
        </div>
      </div>

      {msg && <div className="mp-msg">{msg}</div>}

      {/* Lens 2 result banner (org-scoped) */}
      {respondResult && (
        <div className="mp-section" style={{ background: '#eef9f0', border: '1px solid #9bd1a4' }}>
          <strong>{t('ThreatIntel.responseComplete')}</strong> {respondResult.message}
          <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 13, color: '#334155' }}>
            <span>{t('ThreatIntel.activeSeeds')} <b>{respondResult.activeSeeds}</b></span>
            <span>{t('ThreatIntel.validatedFindings')} <b>{respondResult.validatedNewFindings}</b></span>
            <span>{t('ThreatIntel.confirmedExploitable')} <b style={{ color: '#991b1b' }}>{respondResult.confirmedExploitable}</b></span>
            <span>{t('ThreatIntel.ticketsCreated')} <b>{respondResult.ticketsCreated}</b></span>
            <span>{t('ThreatIntel.skippedExisting')} <b>{respondResult.ticketsSkippedExisting}</b></span>
          </div>
        </div>
      )}

      {/* Scan result banner (org-scoped: probes + Jira + control flag) */}
      {scanResult && (
        <div className="mp-section" style={{ background: '#eef3fc', border: '1px solid #9bb8e8' }}>
          <strong>{t('ThreatIntel.assetScanComplete')}</strong> {scanResult.note}
          <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 13, color: '#334155', flexWrap: 'wrap' }}>
            <span>{t('ThreatIntel.autoCoveredScanned')} <b>{scanResult.autoSeeds}</b></span>
            <span>{t('ThreatIntel.sshBasTriggered')} <b>{scanResult.sshBasScansTriggered}</b></span>
            <span>{t('ThreatIntel.confirmedOnAssets')} <b style={{ color: '#991b1b' }}>{scanResult.confirmedOnAssets}</b></span>
            <span>{t('ThreatIntel.jiraIssues')} <b>{scanResult.jiraIssuesCreated}</b>{scanResult.jiraIntegration ? '' : t('ThreatIntel.noJiraIntegration')}</span>
            <span>{t('ThreatIntel.controlsFlagged')} <b style={{ color: '#9a3412' }}>{scanResult.controlsFlaggedPendingReview}</b></span>
            <span>{t('ThreatIntel.manualSeeds')} <b>{scanResult.manualSeeds}</b></span>
          </div>
        </div>
      )}

      {/* KPI row */}
      <div className="mp-kpi-row">
        <div className="mp-kpi"><div className="mp-kpi-label">{t('ThreatIntel.kpiTotalSeeds')}</div><div className="mp-kpi-value">{counts.total}</div></div>
        <div className="mp-kpi"><div className="mp-kpi-label">{t('ThreatIntel.kpiAutoCovered')}</div><div className="mp-kpi-value" style={{ color: '#065f46' }}>{counts.auto || 0}</div></div>
        <div className="mp-kpi"><div className="mp-kpi-label">{t('ThreatIntel.kpiManual')}</div><div className="mp-kpi-value" style={{ color: '#92400e' }}>{(counts.total - counts.auto) || 0}</div></div>
        <div className="mp-kpi"><div className="mp-kpi-label">{t('ThreatIntel.kpiCritical')}</div><div className="mp-kpi-value" style={{ color: '#991b1b' }}>{counts.CRITICAL || 0}</div></div>
        <div className="mp-kpi"><div className="mp-kpi-label">{t('ThreatIntel.kpiActive')}</div><div className="mp-kpi-value" style={{ color: '#065f46' }}>{counts.active || 0}</div></div>
      </div>

      {/* Lens 1: global seed catalog */}
      <div className="mp-section">
        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>{t('ThreatIntel.seedCatalogHeading')}</h3>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13 }} />
            <input className="mp-input" style={{ paddingLeft: 32, width: '100%' }} placeholder={t('ThreatIntel.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="mp-input" style={{ width: 170 }} value={sevFilter} onChange={e => setSevFilter(e.target.value)}>
            <option value="">{t('ThreatIntel.allSeverities')}</option>
            {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading ? <div className="mp-loading"><i className="fas fa-spinner fa-spin" /> {t('ThreatIntel.loading')}</div> : (
          filtered.length === 0 ? <div className="mp-empty">{t('ThreatIntel.noSeedsFound')}</div> : (
            <table className="mp-table">
              <thead><tr><th>{t('ThreatIntel.thCve')}</th><th>{t('ThreatIntel.thTitle')}</th><th>{t('ThreatIntel.thSeverity')}</th><th>{t('ThreatIntel.thCoverage')}</th><th>{t('ThreatIntel.thStatus')}</th><th>{t('ThreatIntel.thSource')}</th><th>{t('ThreatIntel.thActions')}</th></tr></thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.id || s.cveId}>
                    <td style={{ fontWeight: 600 }}>{s.cveId}</td>
                    <td>{s.title || '—'}</td>
                    <td><Badge value={s.severity} map={SEV_COLORS} /></td>
                    <td>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                        background: (COVERAGE_COLORS[s.coverage] || COVERAGE_COLORS.MANUAL).bg,
                        color: (COVERAGE_COLORS[s.coverage] || COVERAGE_COLORS.MANUAL).text }}
                        title={s.coverage === 'AUTO' ? t('ThreatIntel.coverageTitleAuto') : t('ThreatIntel.coverageTitleManual')}>
                        {COVERAGE_LABEL[s.coverage] || COVERAGE_LABEL.MANUAL}
                      </span>
                    </td>
                    <td>{s.status}</td>
                    <td style={{ fontSize: 11, color: '#94a3b8' }}>{s.source}</td>
                    <td><button className="mp-btn-sm" onClick={() => setSelected(s)}>{t('ThreatIntel.viewButton')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Lens 2: remediation tickets for the org (after Respond) */}
      {tickets.length > 0 && (
        <div className="mp-section">
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>{t('ThreatIntel.remediationTicketsHeading')}</h3>
          <table className="mp-table">
            <thead><tr><th>{t('ThreatIntel.thTitle')}</th><th>{t('ThreatIntel.thSeverity')}</th><th>{t('ThreatIntel.thStatus')}</th><th>{t('ThreatIntel.thDue')}</th></tr></thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id}>
                  <td>{t.title}</td>
                  <td><Badge value={t.severity} map={SEV_COLORS} /></td>
                  <td><Badge value={t.status} map={TICKET_STATUS_COLORS} /></td>
                  <td>{t.dueDate || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Seed detail modal */}
      {selected && (
        <div className="mp-overlay" onClick={() => setSelected(null)}>
          <div className="mp-modal" onClick={e => e.stopPropagation()}>
            <div className="mp-modal-header">
              <h3>{selected.cveId} — {selected.severity}</h3>
              <button onClick={() => setSelected(null)}><i className="fas fa-times" /></button>
            </div>
            <div className="mp-modal-body">
              <div className="mp-field"><label>{t('ThreatIntel.fieldTitle')}</label><div>{selected.title || '—'}</div></div>
              {selected.detectionSignature && (
                <div className="mp-field"><label>{t('ThreatIntel.fieldDetectionSignature')}</label>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, background: '#f8fafc', padding: 10, borderRadius: 8 }}>{selected.detectionSignature}</div>
                </div>
              )}
              {selected.soc2Impact && <div className="mp-field"><label>{t('ThreatIntel.fieldSoc2Impact')}</label><div style={{ fontSize: 13 }}>{selected.soc2Impact}</div></div>}
              {selected.iso27001Impact && <div className="mp-field"><label>{t('ThreatIntel.fieldIso27001Impact')}</label><div style={{ fontSize: 13 }}>{selected.iso27001Impact}</div></div>}
              <div className="mp-field"><label>{t('ThreatIntel.fieldSource')}</label><div>{selected.source} · {selected.status}</div></div>
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-secondary" onClick={() => setSelected(null)}>{t('ThreatIntel.closeButton')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
